/**
 * MVIEW-FIT extent × the group-42 BULGE of a shell-level polyline row.
 *
 * `./extent.ts`'s `"LWPOLYLINE" | "Polyline"` arm contributes the decoded
 * `Vertices` list, which is the CHORD hull of the entity. A bulged span is a
 * circular ARC that bows OUTSIDE its chord, so for the shape a two-vertex
 * bulged polyline really is — a semicircle from `(0, 0)` to `(20, 0)` — the
 * chord hull is the DEGENERATE box `minY === maxY`. `createViewportEntity`
 * gates fitting on `modelW > 0 && modelH > 0`, so that entity fell back to the
 * unrelated `(0, 0)` / viewHeight-100 default view and the new viewport showed
 * nothing at all (@codex PR #785 round 15, FINDING 1).
 *
 * This module supplies the missing bow.
 *
 * # WHICH SIDE OF THE CHORD — the measurement, not an impression
 *
 * A bulge's SIGN says which side its arc bows to, so an extent that puts the
 * bow on the wrong side is worse than no bow at all: it frames a shape that is
 * nowhere on the canvas and leaves the real one outside the viewport.
 *
 * For `v1 = (0, 0)`, `v2 = (20, 0)`, `bulge = +1` (a semicircle):
 *
 *   | source                                                    | apex       |
 *   |-----------------------------------------------------------|------------|
 *   | Autodesk DXF reference (LWPOLYLINE 42): the bulge is       | (10, -10)  |
 *   | NEGATIVE iff the arc runs CW from start to end, so `+1`    |            |
 *   | runs CCW — 180° -> 270° -> 360° about the centre (10, 0)   |            |
 *   | `ezdxf` 1.4.4 `ezdxf.math.bulge_to_arc`, executed:         | (10, -10)  |
 *   | centre (10, 0), r 10, start 180°, end 0°, traversed CCW    |            |
 *   | THIS repo's renderer — `apps/web/.../scene3d/adapters/     | (10, -10)  |
 *   | arcEllipseSpline.ts::bulgeToArc`, `ccw = sign(bulge) > 0`  |            |
 *   | `drawing-model/bulgeArc.ts::bulgeArc` (r17 P1 fix applied) | (10, -10)  |
 *
 * All four now agree. HISTORY — this module was written while they did NOT:
 * `bulgeArc` was MIRRORED about the chord in BOTH the centre side
 * (`centerOffset = -bulgeSign * ...`) and the sweep direction
 * (`signedSweep = -bulgeSign * ...`), so a single {@link bulgeSegmentBounds}
 * call failed to contain the DRAWN arc in 37 750 of 40 000 generated
 * (chord, bulge) pairs measured against the renderer's own `bulgeToArc` at
 * 4096 stations. This arm therefore took the CHORD-SYMMETRIC HULL — the union
 * of `bulgeSegmentBounds` at `+bulge` AND at `-bulge`, a set invariant under
 * exactly the reflection the bug was — so that the extent was right whichever
 * side the primitive bowed to.
 *
 * # WHY THAT WORKAROUND IS GONE (r17 P1)
 *
 * `bulgeArc` has been corrected to the canonical orientation, so the union's
 * second half is no longer protection — it is a pure OVER-REPORT of exactly
 * the mirrored sagitta. For the reference semicircle the hull spans
 * `y[-10, +10]` while the drawing occupies only `y[-10, 0]`: MVIEW FIT would
 * frame TWICE the height the entity actually has, and ZOOM/FIT framing that
 * does not match what AutoCAD frames is the kind of simplification CLAUDE.md
 * ПРИОРИТЕТ #1 forbids. The arm is therefore the single `+bulge` call.
 *
 * Coverage is not lost, and that is measured rather than asserted: over the
 * same 40 000 generated (chord, bulge) pairs sampled against the renderer's
 * own `bulgeToArc` at 4096 stations, the single-call box contains the drawn
 * arc in 40 000 of 40 000 cases (0 misses) — the identical score the symmetric
 * hull had — while its area drops to a mean 0.61 x the hull's. (Re-run on the
 * corrected primitive, the MIRRORED box that `bulgeArc` used to return misses
 * in 37 230 of those same 40 000 — the defect, measured from the other side.)
 * `./extentPolylineBulge.property.test.ts` pins BOTH halves as properties:
 * P-E1 containment against an ezdxf-transcribed oracle, and P-E3 the chord
 * REFLECTION metamorphic relation that replaced the old sign-invariance
 * (which is now false by construction, as it must be — an extent that cannot
 * tell `+b` from `-b` cannot be reporting the side either).
 */

import { bulgeSegmentBounds } from "@dxf-ai-editor/drawing-model";

/** A decoded `Vertices` row entry — `z` is carried by the parser and unused here. */
type ExtentVertex = { x: number; y: number };

/**
 * The two opposite corners of the drawn-arc box of every BULGED span of the
 * given vertex run, or an empty array when no span bows.
 *
 * Only the hull CORNERS are returned because the single consumer
 * (`./extent.ts::computeModelExtent`) folds a min/max over the points it is
 * handed — two opposite corners pin an axis-aligned box exactly, and the other
 * two are redundant there.
 *
 * `bulges[i]` is the group-42 value of the span LEAVING vertex `i` (the DXF
 * convention, the same one `./pathSampling.ts::polylineSegments` and
 * `./geometryEntityTypes.ts::pathSpans` read). A `closed` run additionally
 * draws the wrap span from the LAST vertex back to the first, bowed by the
 * last vertex's own bulge.
 *
 * COUNT-LOCKED: a `bulges` list whose length does not match `vertices` is
 * ignored ENTIRELY rather than applied by index. A short or long list would
 * put a bow on a span that does not have one and leave the span that does
 * straight, i.e. frame a shape the drawing does not contain — strictly worse
 * than the chord hull this function is adding to. The same count lock the
 * `Bulges` property editor enforces (`./extendedPropertyMutations.ts`).
 *
 * A span whose bulge is 0 (or whose two vertices coincide) contributes
 * nothing: `bulgeSegmentBounds` returns the endpoint box for it, and both
 * endpoints are already in the caller's point list.
 */
export function bulgedSpanHullCorners(
  vertices: readonly ExtentVertex[],
  bulges: readonly number[],
  closed: boolean,
): ExtentVertex[] {
  const out: ExtentVertex[] = [];
  if (vertices.length < 2) return out;
  if (bulges.length !== vertices.length) return out;

  const spanCount = closed ? vertices.length : vertices.length - 1;
  for (let i = 0; i < spanCount; i++) {
    const bulge = bulges[i]!;
    // `bulgeSegmentBounds`' own straight-span guard is `Math.abs(bulge) < EPS`
    // with `EPS = 1e-12`; testing `!== 0` here would only mean evaluating an
    // arc walk that comes back as the endpoint box. The guard is a SKIP,
    // never a different answer, so it cannot change the extent.
    if (!Number.isFinite(bulge) || bulge === 0) continue;
    const v1 = vertices[i]!;
    const v2 = vertices[(i + 1) % vertices.length]!;
    // The box of the arc as DRAWN — one sign, the authored one. See the module
    // doc for why the chord-symmetric union this used to take is gone.
    const bowed = bulgeSegmentBounds(v1, v2, bulge);
    out.push({ x: bowed.minX, y: bowed.minY });
    out.push({ x: bowed.maxX, y: bowed.maxY });
  }
  return out;
}
